import React from 'react';

import { Col, Form, Input, Row, Select, Typography } from 'antd';

import './edit-contract.scss';
import { EditContractFragmentProps } from 'fragments/contract-card/edit-contract/interfaces';
import { useEditContractController } from 'fragments/contract-card/edit-contract/edit-contract.controller';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import ModalForm from 'components/modal-form/modal-form.component';
import SelectOrCreateItem from 'components/select-or-create-item/select-or-create-item.component';
import CreatePropertyFields from 'components/create-property-fields/create-property-fields.component';
import CreatePersonsFields from 'components/create-persons-fields/create-persons-fields.component';
import { fixSelectOnScroll } from 'tools/view-hooks/fix-autocomplete';

export const EditContractFragment: React.FC<EditContractFragmentProps> = (props) => {
    const { useController = useEditContractController } = props;
    const controller = useController(props.onFinish);
    const { translate } = useTranslator();

    return (
        <ModalForm
            className={'edit-contract'}
            onFinish={controller.onFormSubmit}
            form={controller.form}
            isvisible={props.isVisible}
            onCancel={props.onCancel}
            isLoading={controller.isLoading}
            layout={'vertical'}
            initialValues={controller.initialValues}
            title={translate({ key: 'contracts.edit-contract' })}
        >
            <SelectOrCreateItem
                createFormItemName={'newProperty'}
                selectOptions={controller.propertiesOptions}
                optionFilterProp={'label'}
                createComponent={
                    <CreatePropertyFields
                        personsOptions={controller.personsOptions}
                        provincesOptions={controller.provincesOptions}
                    />
                }
                createTitle={translate({ key: 'contracts.property-data' })}
                name={'propertyId'}
                label={translate({ key: 'contracts.properties' })}
                requiredForm={true}
                rulesComponent={[
                    {
                        validator: (_: any, value: any, callback: any) => {
                            if (
                                !value ||
                                !value.street ||
                                !value.district ||
                                !value.provinceId ||
                                !value.city ||
                                !value.owners ||
                                (value.owners === -1 &&
                                    (!value.newOwner ||
                                        !value.newOwner.name ||
                                        !value.newOwner.lastName ||
                                        !value.newOwner.numberDoc))
                            ) {
                                callback('Complete los datos requeridos');
                                return;
                            }
                            callback();
                        },
                    },
                ]}
            />
            {/* Editing the address is a correction of the currently linked property's own data, so it only shows
                while that property stays selected — picking a different one or "Crear Nuevo" replaces the link
                instead, which is a different action with its own fields above. */}
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.propertyId !== curr.propertyId}>
                {() =>
                    controller.form.getFieldValue('propertyId') === controller.linkedPropertyId ? (
                        <div className={'edit-contract-address'}>
                            <Typography.Title level={5}>
                                {translate({ key: 'contracts.property-data' })}
                            </Typography.Title>
                            <Row gutter={10}>
                                <Col xs={{ span: 24 }} xl={{ span: 12 }}>
                                    <Form.Item
                                        label={translate({ key: 'properties.street' })}
                                        name="street"
                                        rules={[{ required: true }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 12 }} md={{ span: 12 }} xl={{ span: 6 }}>
                                    <Form.Item label={translate({ key: 'properties.number' })} name="number">
                                        <Input type="number" placeholder={'S/N'} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 12 }} md={{ span: 12 }} xl={{ span: 6 }}>
                                    <Form.Item
                                        label={translate({ key: 'properties.floor-department' })}
                                        name="floorDepartment"
                                    >
                                        <Input style={{ textTransform: 'uppercase' }} />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} md={{ span: 12 }}>
                                    <Form.Item
                                        label={translate({ key: 'properties.district' })}
                                        name="district"
                                        rules={[{ required: true }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} md={{ span: 12 }}>
                                    <Form.Item label={translate({ key: 'properties.postal-code' })} name="postalCode">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} md={{ span: 12 }} id="select-search">
                                    <Form.Item
                                        name="provinceId"
                                        label={translate({ key: 'settings.province' })}
                                        rules={[{ required: true }]}
                                    >
                                        <Select
                                            allowClear
                                            showSearch
                                            options={controller.provincesOptions}
                                            optionFilterProp="label"
                                            getPopupContainer={fixSelectOnScroll}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={{ span: 24 }} md={{ span: 12 }}>
                                    <Form.Item
                                        name="city"
                                        label={translate({ key: 'settings.city' })}
                                        rules={[{ required: true }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    ) : null
                }
            </Form.Item>
            <SelectOrCreateItem
                name={'rentersId'}
                createFormItemName={'newRenter'}
                selectOptions={controller.personsOptions}
                selectMode={'multiple'}
                optionFilterProp={'label'}
                onClickSelect={controller.onClickSelect}
                isOpen={controller.selectIsOpen}
                onBlurSelect={controller.onBlurSelect}
                createComponent={<CreatePersonsFields provincesOptions={controller.provincesOptions} />}
                createTitle={translate({ key: 'contracts.renter-data' })}
                label={translate({ key: 'contracts.renters' })}
                requiredForm={true}
                rulesComponent={[
                    {
                        validator: (_: any, value: any, callback: any) => {
                            if (!value || !value.name || !value.lastName || !value.numberDoc) {
                                callback('Complete los datos requeridos');
                                return;
                            }
                            callback();
                        },
                    },
                ]}
            />
        </ModalForm>
    );
};
